import { apiRequest } from '../api/http'
import type {
  AdminApiCallMetricsParams,
  AdminApiCallMetricsResponse,
  AdminAttentionResponse,
  AdminConnectionSummary,
  AdminFailedScheduledTask,
  AdminOrganizationSummary,
  AdminProviderProxyRequest,
  AdminProviderProxyResponse,
  AdminQuotaSummary,
  AdminSallaInvitation,
  AdminUserSummary,
} from './types'
import type { StockAllocationItemPage, StockAllocationRunDetail, StockAllocationRunSummary } from '../stockAllocation/types'

const ADMIN_BASE = '/api/admin'

function adminAuth(accessToken: string) {
  return { accessToken }
}

function metricsQuery(params: AdminApiCallMetricsParams = {}): string {
  const search = new URLSearchParams()
  if (params.organizationId != null) search.set('organizationId', String(params.organizationId))
  if (params.provider?.trim()) search.set('provider', params.provider.trim())
  if (params.from?.trim()) search.set('from', params.from.trim())
  if (params.to?.trim()) search.set('to', params.to.trim())
  const query = search.toString()
  return query ? `?${query}` : ''
}

export function listAdminOrganizations(accessToken: string): Promise<AdminOrganizationSummary[]> {
  return apiRequest<AdminOrganizationSummary[]>(ADMIN_BASE, '/directory/organizations', adminAuth(accessToken))
}

export function listAdminSallaInvitations(accessToken: string): Promise<AdminSallaInvitation[]> {
  return apiRequest<AdminSallaInvitation[]>(ADMIN_BASE, '/salla/invitations', adminAuth(accessToken))
}

export function resendAdminSallaInvitation(accessToken: string, merchantId: string): Promise<AdminSallaInvitation> {
  return apiRequest<AdminSallaInvitation>(ADMIN_BASE, `/salla/invitations/${encodeURIComponent(merchantId)}/resend`, {
    ...adminAuth(accessToken),
    method: 'POST',
  })
}

export function getAdminOrganization(accessToken: string, organizationId: number): Promise<AdminOrganizationSummary> {
  return apiRequest<AdminOrganizationSummary>(ADMIN_BASE, `/directory/organizations/${organizationId}`, adminAuth(accessToken))
}

export function listAdminOrganizationUsers(accessToken: string, organizationId: number): Promise<AdminUserSummary[]> {
  return apiRequest<AdminUserSummary[]>(ADMIN_BASE, `/directory/organizations/${organizationId}/users`, adminAuth(accessToken))
}

export function listAdminChannelConnections(accessToken: string, organizationId: number): Promise<AdminConnectionSummary[]> {
  return apiRequest<AdminConnectionSummary[]>(ADMIN_BASE, `/directory/organizations/${organizationId}/channel-connections`, adminAuth(accessToken))
}

export function listAdminInventoryConnections(accessToken: string, organizationId: number): Promise<AdminConnectionSummary[]> {
  return apiRequest<AdminConnectionSummary[]>(ADMIN_BASE, `/directory/organizations/${organizationId}/inventory-connections`, adminAuth(accessToken))
}

export function updateAdminOrganizationProviderQuota(accessToken: string, organizationId: number, provider: string, callLimit: number): Promise<AdminQuotaSummary> {
  return apiRequest<AdminQuotaSummary>(ADMIN_BASE, `/directory/organizations/${organizationId}/provider-quotas/${encodeURIComponent(provider)}`, {
    ...adminAuth(accessToken),
    method: 'PUT',
    body: { callLimit },
  })
}

export function resetAdminOrganizationProviderQuota(accessToken: string, organizationId: number, provider: string): Promise<AdminQuotaSummary> {
  return apiRequest<AdminQuotaSummary>(ADMIN_BASE, `/directory/organizations/${organizationId}/provider-quotas/${encodeURIComponent(provider)}`, {
    ...adminAuth(accessToken),
    method: 'DELETE',
  })
}

export function getAdminApiCallMetrics(accessToken: string, params: AdminApiCallMetricsParams = {}): Promise<AdminApiCallMetricsResponse> {
  return apiRequest<AdminApiCallMetricsResponse>(ADMIN_BASE, `/metrics/api-calls${metricsQuery(params)}`, adminAuth(accessToken))
}

export function getAdminAttention(accessToken: string): Promise<AdminAttentionResponse> {
  return apiRequest<AdminAttentionResponse>(ADMIN_BASE, '/attention', adminAuth(accessToken))
}

export function listAdminFailedScheduledTasks(accessToken: string): Promise<AdminFailedScheduledTask[]> {
  return apiRequest<AdminFailedScheduledTask[]>(ADMIN_BASE, '/scheduled-tasks/failed', adminAuth(accessToken))
}

export function retryAdminScheduledTask(accessToken: string, taskName: string, instanceId: string): Promise<void> {
  return apiRequest<void>(ADMIN_BASE, `/scheduled-tasks/${encodeURIComponent(taskName)}/${encodeURIComponent(instanceId)}/retry`, {
    ...adminAuth(accessToken),
    method: 'POST',
  })
}

export function listAdminStockAllocationRuns(accessToken: string): Promise<StockAllocationRunSummary[]> {
  return apiRequest<StockAllocationRunSummary[]>(ADMIN_BASE, '/stock-allocation-runs', adminAuth(accessToken))
}

export function getAdminStockAllocationRun(accessToken: string, runId: number): Promise<StockAllocationRunDetail> {
  return apiRequest<StockAllocationRunDetail>(ADMIN_BASE, `/stock-allocation-runs/${runId}`, adminAuth(accessToken))
}

export function listAdminStockAllocationRunItems(accessToken: string, runId: number): Promise<StockAllocationItemPage> {
  return apiRequest<StockAllocationItemPage>(ADMIN_BASE, `/stock-allocation-runs/${runId}/items?status=FAILED&size=100`, adminAuth(accessToken))
}

export function retryAdminStockAllocationRun(accessToken: string, runId: number): Promise<StockAllocationRunDetail> {
  return apiRequest<StockAllocationRunDetail>(ADMIN_BASE, `/stock-allocation-runs/${runId}/retry-failed`, {
    ...adminAuth(accessToken), method: 'POST',
  })
}

export function proxyAdminChannelProvider(accessToken: string, connectionId: number, request: AdminProviderProxyRequest): Promise<AdminProviderProxyResponse> {
  return apiRequest<AdminProviderProxyResponse>(ADMIN_BASE, `/providers/${connectionId}/proxy`, {
    ...adminAuth(accessToken),
    method: 'POST',
    body: request,
  })
}

export function proxyAdminInventoryProvider(accessToken: string, connectionId: number, request: AdminProviderProxyRequest): Promise<AdminProviderProxyResponse> {
  return apiRequest<AdminProviderProxyResponse>(ADMIN_BASE, `/inventory-providers/${connectionId}/proxy`, {
    ...adminAuth(accessToken),
    method: 'POST',
    body: request,
  })
}
